'use client'

import { memo, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useFeatureFlags } from '@node-space/hooks'
import { Currency, isBvnkCurrency, isLayer1Currency, ProtocolCode } from '@/types/currency'
import { PaymentSummary } from '@/types/payment'
import { BVNK } from '@/utils/constants'
import { DisclaimerWrapper } from './DisclaimerWrapper'

interface PaymentDisclaimerProps {
  isMultiProtocolCurrency: boolean
  paymentSummary?: PaymentSummary
  selectedProtocol: string
  selectedCurrencyCode: string
  currencies: Currency[]
}

const PaymentDisclaimer = ({
  isMultiProtocolCurrency,
  selectedProtocol,
  selectedCurrencyCode,
  paymentSummary,
  currencies,
}: PaymentDisclaimerProps) => {
  const t = useTranslations()
  const pathName = usePathname()
  const { enableCopyTextDeelPayOut } = useFeatureFlags()

  const isPayin = pathName?.includes('payin')

  let currencyKey = ''
  let networkKey = ''
  if (BVNK) {
    currencyKey = selectedCurrencyCode.toUpperCase()
  } else {
    currencyKey = selectedCurrencyCode.split('-')[0]
    networkKey = selectedCurrencyCode.split('-')[1]
  }

  const extractedCodeAndProtocols = useMemo(
    () =>
      currencies?.find(
        currency =>
          (isBvnkCurrency(currency) && currency?.code === currencyKey) ||
          (isLayer1Currency(currency) &&
            currency?.code === currencyKey &&
            currency?.network === networkKey)
      ),
    [currencies, currencyKey, networkKey]
  )

  const getNetworkName = (currency: Currency | undefined): string | undefined => {
    if (!currency) return undefined

    if (isBvnkCurrency(currency)) {
      return currency.enabledProtocols?.[0]?.network
    }

    if (isLayer1Currency(currency)) {
      return currency.network
    }

    return undefined
  }

  const paymentProtocol = paymentSummary?.address?.protocol?.toUpperCase()

  if (selectedProtocol || (!isMultiProtocolCurrency && paymentProtocol)) {
    let protocolDisclaimerKey
    if (paymentProtocol === ProtocolCode.POLYGON.toUpperCase()) {
      protocolDisclaimerKey = isPayin
        ? 'disclaimerPayInProtocolPolygon'
        : 'disclaimerPayOutProtocolPolygon'
    } else {
      if (isPayin) {
        protocolDisclaimerKey = 'disclaimerPayInProtocol'
      } else if (enableCopyTextDeelPayOut) {
        protocolDisclaimerKey = 'disclaimerDeelPayOutProtocol'
      } else {
        protocolDisclaimerKey = 'disclaimerPayOutProtocol'
      }
    }

    return (
      <DisclaimerWrapper>
        {t(`${protocolDisclaimerKey}`, {
          currencyCode: extractedCodeAndProtocols?.code,
          protocol: selectedProtocol?.toUpperCase() || paymentProtocol,
          prefix: extractedCodeAndProtocols?.code === 'USDC' ? 'Native' : '',
        })}
      </DisclaimerWrapper>
    )
  }

  let networkDisclaimerKey

  if (isPayin) {
    networkDisclaimerKey = 'disclaimerPayInNetwork'
  } else if (enableCopyTextDeelPayOut) {
    networkDisclaimerKey = 'disclaimerDeelPayOutNetwork'
  } else {
    networkDisclaimerKey = 'disclaimerPayOutNetwork'
  }

  return (
    <DisclaimerWrapper>
      {t(`${networkDisclaimerKey}`, {
        currencyCode: extractedCodeAndProtocols?.code,
        network: getNetworkName(extractedCodeAndProtocols),
      })}
    </DisclaimerWrapper>
  )
}

export default memo(PaymentDisclaimer)
